/**
 * The minimum a rate has to expose for these decisions. Declared structurally
 * rather than as the full `LaborRate` so the editor's narrower in-memory rate
 * list (`{ id, hourly_rate }[]`) is accepted without a cast that would hide a
 * real mismatch.
 */
export interface RateChoice {
  id: number;
  hourly_rate: number;
  is_default?: number | boolean | null;
}

/**
 * Where a new line's labor rate comes from.
 *
 * Two things in this app both call themselves "the shop's labor rate":
 *
 *   - `labor_rates` — the named rates an owner configures (Standard, Diesel, …),
 *     one of which carries `is_default`. This is what the line's
 *     `labor_rate_id` points at and what the picker displays.
 *   - `shop_settings.labor_rate` — a single scalar that predates named rates.
 *
 * Seeding `labor_rate_id` from the first and `hourly_rate` from the second
 * produced a line that displayed one rate and charged another. The named rate
 * wins wherever one applies; the scalar survives only as the fallback for a
 * shop that has configured no named rates at all.
 */

/**
 * The named rate a new line should point at, or `null` when the shop's
 * configuration does not identify one.
 *
 * A single configured rate is treated as the default even without the flag —
 * with one option there is nothing else it could mean. Two or more unflagged
 * rates are genuinely ambiguous, and guessing would silently price work at
 * whichever happened to be first.
 */
export function defaultNamedRate(rates: RateChoice[] | null | undefined): RateChoice | null {
  if (!Array.isArray(rates) || rates.length === 0) return null;
  const flagged = rates.find((r) => Boolean((r as any).is_default));
  if (flagged) return flagged;
  return rates.length === 1 ? rates[0] : null;
}

/**
 * The hourly rate a new line should carry, paired with `defaultNamedRate`'s id.
 *
 * `shopFallbackRate` is `shop_settings.labor_rate`, used only when no named
 * rate applies or the one that does carries an unusable figure.
 */
export function seedHourlyRate(
  rates: RateChoice[] | null | undefined,
  shopFallbackRate: number | null | undefined,
): number {
  const named = defaultNamedRate(rates);
  const namedRate = Number(named?.hourly_rate);
  if (Number.isFinite(namedRate) && namedRate > 0) return namedRate;
  const fallback = Number(shopFallbackRate);
  return Number.isFinite(fallback) ? fallback : 0;
}
